/**
 * AdminActionsFab — iter216.
 *
 * The admin-actions Sheet for a group. Two entry modes:
 *
 *   1) Controlled (preferred, from PrimaryActionBar "⚙️ Manage" chip):
 *      <AdminActionsFab open={x} onOpenChange={setX} noFab {...} />
 *
 *   2) Legacy FAB (kept as fallback / mobile long-press affordance):
 *      <AdminActionsFab {...} />   → renders the floating "+" button too.
 *
 * iter216 UX overhaul:
 *   • MERGED "Top Up" + "Allot Money" into a single **Money Action** tile.
 *     Clicking it opens a small chooser sheet with the Cash-vs-Ledger toggle.
 *   • Collapsed the rarely-used Imports / Fine defaults / Balance limits into
 *     a "More…" section, tap to expand.
 *   • Renamed section header to just "Money movements" (single unified block).
 */
import { useState } from 'react';
import {
  Plus,
  Clock,
  AlertTriangle,
  CreditCard,
  Ban,
  UserMinus,
  ArrowUpRight,
  Zap,
  UserPlus,
  Settings,
  ArrowDownLeft,
  RefreshCw,
  Trash2,
  Wallet,
  Scale,
  ChevronDown,
  ChevronUp,
  HandCoins,
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from './ui/sheet';
import NXCIcon from './NXCIcon';

export default function AdminActionsFab({
  isAdmin,
  fineDefaults = {},
  importLoading,
  ctx,
  // iter216 — controlled-mode props (from PrimaryActionBar "Manage" chip)
  open: openProp,
  onOpenChange,
  noFab = false,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (v) => {
    if (isControlled) onOpenChange?.(v);
    else setInternalOpen(v);
  };

  // Money-Action chooser (iter216) — small sub-sheet asks the cash-vs-ledger Q
  const [moneyChooserOpen, setMoneyChooserOpen] = useState(false);
  // "More…" expand toggle for imports / configure / danger
  const [moreOpen, setMoreOpen] = useState(false);

  if (!isAdmin) return null;

  // Resolve callbacks (every action falls back to a no-op so missing handlers
  // never throw — the FAB is always safe to mount).
  const noop = () => {};
  const {
    setShowFundModal = noop,
    openFineWithType = noop,
    setShowAllotCoins = noop,
    setShowTransferModal = noop,
    setShowGuestContribModal = noop,
    setShowLoanToMemberModal = noop,
    openQuickSettle = noop,
    handleImportFile = noop,
    handleImportFines = noop,
    handleDownloadSample = noop,
    setShowFineDefaultsModal = noop,
    setShowBalancePolicyModal = noop,
    setShowMembershipModal = noop,
    handleResetGroup = noop,
    group,
    user,
  } = ctx || {};

  // iter235 — Ledger allotment is a super-admin repair tool only; group
  // admins go straight to the cash-in flow.
  const isSuperAdmin = !!user?.is_super_admin;

  // Feature flags drive which sections render. Default to "show" if not provided
  // (legacy ctx may not carry group.features yet).
  const features = group?.features || {};
  const showFines = features.fines !== false;
  const showLoans = features.loans !== false;

  const close = () => setOpen(false);
  const ten = fineDefaults.late_threshold_minutes ?? 10;

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {!noFab && (
          <SheetTrigger asChild>
            <button
              data-testid="admin-fab"
              aria-label="Admin actions"
              title="Admin actions"
              className="fixed bottom-24 right-5 z-30 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-2xl shadow-primary/30 flex items-center justify-center active:scale-95 hover:scale-105 transition-transform"
            >
              <Settings className="w-6 h-6" strokeWidth={2.5} />
            </button>
          </SheetTrigger>
        )}

        <SheetContent
          side="bottom"
          className="rounded-t-3xl px-5 pb-7 pt-4 max-h-[85vh] overflow-y-auto"
          data-testid="admin-fab-sheet"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg font-bold tracking-tight">Manage group</SheetTitle>
            <p className="text-xs text-muted-foreground -mt-1">
              Money movements, fines, and imports for this group.
            </p>
          </SheetHeader>

          {/* iter216 — PRIMARY: Money movements (5 tiles, unified Money Action) */}
          <Section title="Money movements" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <ActionTile
              testId="fab-money-action"
              label={isSuperAdmin ? 'Money Action' : 'Cash In'}
              sub={isSuperAdmin ? 'Cash-in OR ledger fix' : 'Money into the kitty'}
              Icon={Wallet}
              tone="brand"
              onClick={() => {
                close();
                if (isSuperAdmin) setMoneyChooserOpen(true);
                else setShowFundModal(true);
              }}
            />
            <ActionTile
              testId="fab-transfer"
              label="Transfer"
              sub="Between two members"
              Icon={ArrowUpRight}
              onClick={() => { close(); setShowTransferModal(true); }}
            />
            <ActionTile
              testId="fab-quick-settle"
              label="Settle"
              sub="Zero out balances"
              Icon={Zap}
              tone="brand"
              onClick={() => { close(); openQuickSettle(); }}
            />
            <ActionTile
              testId="fab-guest-contrib"
              label="Guest contribution"
              sub="Cash from a non-member"
              Icon={UserPlus}
              onClick={() => { close(); setShowGuestContribModal(true); }}
            />
            {showLoans && (
              <ActionTile
                testId="fab-loan-to-member"
                label="Loan to member"
                sub="Group lends from fund"
                Icon={HandCoins}
                onClick={() => { close(); setShowLoanToMemberModal(true); }}
              />
            )}
          </div>

          {/* FINES — unchanged */}
          {showFines && (
          <>
            <Section title="Fines" />
            <div className="grid grid-cols-4 gap-2">
              <ActionTile
                testId="fab-late-under"
                label={`Late ≤${ten}m`}
                sub={`${Math.round(fineDefaults.late_10 ?? 100)}`}
                Icon={Clock}
                tone="amber"
                onClick={() => { close(); openFineWithType('late_10'); }}
              />
              <ActionTile
                testId="fab-late-over"
                label={`Late >${ten}m`}
                sub={`${Math.round(fineDefaults.late_above_10 ?? 200)}`}
                Icon={AlertTriangle}
                tone="amber"
                onClick={() => { close(); openFineWithType('late_above_10'); }}
              />
              <ActionTile
                testId="fab-cancel"
                label="Cancel"
                sub={`${Math.round(fineDefaults.cancellation ?? 300)}`}
                Icon={Ban}
                tone="rose"
                onClick={() => { close(); openFineWithType('cancellation'); }}
              />
              <ActionTile
                testId="fab-noshow"
                label="No-show"
                sub={`${Math.round(fineDefaults.no_show ?? 500)}`}
                Icon={UserMinus}
                tone="rose"
                onClick={() => { close(); openFineWithType('no_show'); }}
              />
            </div>
          </>
          )}

          {/* iter216 — MORE… (collapsible section for rarely-used actions) */}
          <button
            type="button"
            data-testid="fab-more-toggle"
            onClick={() => setMoreOpen((v) => !v)}
            className="mt-5 w-full flex items-center justify-between px-1 py-2 text-xs font-bold tracking-wider text-muted-foreground hover:text-foreground transition"
          >
            <span className="uppercase tracking-[0.16em]">More…</span>
            {moreOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {moreOpen && (
            <div data-testid="fab-more-panel">
              {/* IMPORTS */}
              <p className="mt-1 mb-1.5 text-xs uppercase tracking-[0.12em] text-muted-foreground/70 font-semibold">Bulk imports</p>
              <div className="grid grid-cols-2 gap-2">
                <SheetClose asChild>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleImportFile}
                      className="hidden"
                      data-testid="fab-import-balances"
                    />
                    <ImportTile
                      label="Balances"
                      Icon={ArrowDownLeft}
                      disabled={false}
                    />
                  </label>
                </SheetClose>
                <SheetClose asChild>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleImportFines}
                      disabled={!!importLoading}
                      className="hidden"
                      data-testid="fab-import-fines"
                    />
                    <ImportTile
                      label={importLoading === 'preview' ? 'Parsing…' : 'Fines'}
                      Icon={importLoading === 'preview' ? RefreshCw : ArrowDownLeft}
                      disabled={!!importLoading}
                      spin={importLoading === 'preview'}
                    />
                  </label>
                </SheetClose>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownloadSample}
                data-testid="fab-download-sample"
                className="w-full mt-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                Download sample CSV →
              </Button>

              {/* CONFIGURE */}
              <p className="mt-3 mb-1.5 text-xs uppercase tracking-[0.12em] text-muted-foreground/70 font-semibold">Configure</p>
              <div className="grid grid-cols-2 gap-2">
                <ActionTile
                  testId="fab-fine-defaults"
                  label="Fine defaults"
                  Icon={Settings}
                  onClick={() => { close(); setShowFineDefaultsModal(true); }}
                />
                <ActionTile
                  testId="fab-balance-policy"
                  label="Balance limits"
                  Icon={AlertTriangle}
                  onClick={() => { close(); setShowBalancePolicyModal(true); }}
                />
                <ActionTile
                  testId="fab-membership-fee"
                  label="Membership fee"
                  Icon={CreditCard}
                  onClick={() => { close(); setShowMembershipModal(true); }}
                />
              </div>

              {/* DANGER ZONE */}
              <button
                onClick={() => { close(); handleResetGroup(); }}
                data-testid="fab-reset-group"
                className="mt-4 w-full rounded-2xl border border-rose-500/30 text-rose-500 hover:bg-rose-500/10 active:scale-[0.99] transition py-2.5 text-xs font-bold tracking-wide flex items-center justify-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Reset group data
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* iter216 — Money Action chooser (cash-in vs ledger adjustment) */}
      <Sheet open={moneyChooserOpen} onOpenChange={setMoneyChooserOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl px-5 pb-7 pt-4"
          data-testid="money-action-chooser"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg font-bold tracking-tight">Money Action</SheetTitle>
            <p className="text-xs text-muted-foreground -mt-1">
              What are you doing? Pick the option that best describes reality.
            </p>
          </SheetHeader>

          <div className="grid grid-cols-1 gap-2 mt-3">
            <button
              type="button"
              data-testid="money-action-cash"
              onClick={() => {
                setMoneyChooserOpen(false);
                setShowFundModal(true);
              }}
              className="text-left rounded-2xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 active:scale-[0.99] transition px-4 py-3.5 flex items-start gap-3"
            >
              <span className="mt-0.5 w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Wallet className="w-4 h-4 text-primary" strokeWidth={2.5} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">💵 Someone paid cash into the group kitty</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                  Real money is being added to the fund. Choose a contributor
                  (member, equal split, or external donation) in the next step.
                </p>
              </div>
            </button>

            <button
              type="button"
              data-testid="money-action-ledger"
              onClick={() => {
                setMoneyChooserOpen(false);
                setShowAllotCoins(true);
              }}
              className="text-left rounded-2xl border-2 border-border bg-card hover:bg-accent active:scale-[0.99] transition px-4 py-3.5 flex items-start gap-3"
            >
              <span className="mt-0.5 w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                <Scale className="w-4 h-4 text-foreground/70" strokeWidth={2.5} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold">⚖️ Adjust a member&apos;s ledger (no cash moved)</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                  Fix an opening balance, apply a gift/bonus, or correct a
                  ledger error. The group fund is NOT touched.
                </p>
              </div>
            </button>
          </div>

          <p className="mt-3 text-xs text-muted-foreground/80 leading-snug">
            <strong>Not sure?</strong> If actual cash / UPI / bank transfer
            hits someone&apos;s hands, pick the first option. If it&apos;s
            just a bookkeeping fix, pick the second.
          </p>
        </SheetContent>
      </Sheet>
    </>
  );
}

function Section({ title }) {
  return (
    <p className="mt-5 mb-2 text-xs uppercase tracking-[0.16em] text-muted-foreground font-bold">
      {title}
    </p>
  );
}

function ActionTile({ testId, label, sub, Icon, onClick, tone = 'neutral' }) {
  const toneClass =
    tone === 'brand' ? 'border-primary/40 bg-primary/5 hover:bg-primary/10 text-foreground'
  : tone === 'amber' ? 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 text-foreground'
  : tone === 'rose'  ? 'border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/10 text-foreground'
  :                    'border-border bg-card hover:bg-accent text-foreground';
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`rounded-2xl border-2 ${toneClass} p-3 text-left transition active:scale-[0.97] flex flex-col gap-1 min-h-[80px]`}
    >
      <Icon className={`w-4 h-4 ${tone === 'brand' ? 'text-primary' : tone === 'amber' ? 'text-amber-500' : tone === 'rose' ? 'text-rose-500' : 'text-foreground/70'}`} strokeWidth={2.25} />
      <div>
        <p className="text-[12px] font-bold tracking-tight leading-tight">{label}</p>
        {sub && <p className="text-xs text-muted-foreground tabular-nums leading-tight mt-0.5">{sub}</p>}
      </div>
    </button>
  );
}

function ImportTile({ label, Icon, disabled, spin = false }) {
  return (
    <span
      className={`rounded-2xl border-2 border-border bg-card p-3 flex flex-col items-start gap-1 min-h-[80px] ${disabled ? 'opacity-50 pointer-events-none' : 'hover:bg-accent active:scale-[0.97]'} transition`}
    >
      <Icon className={`w-4 h-4 text-foreground/70 ${spin ? 'animate-spin' : ''}`} strokeWidth={2.25} />
      <p className="text-[12px] font-bold tracking-tight leading-tight">{label}</p>
    </span>
  );
}
